import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mixpanelTrack, getLogoNameBrandName, gainsightTrack } from 'utils';
import { AdminSmallSwitch, Icon } from 'learning-panel-components';
import { translate } from 'translations';
import NotificationsContainer from '@components/layout/notifications/notificationsContainer';
import { useGetReportsStatus } from '@queries/core/reports';
import { useSelector } from 'react-redux';
import { selectToken, selectCustomBrandName } from '@redux/slices/userSlice';
import { ReportStatus, ReportToStatus } from 'network-utils';
import styles from './topBar.module.scss';
import DownloadHubButton, { DownloadHubButtonStatus } from '../downloadHub/downloadHubButton';
import DownloadHubContainer from '../downloadHub/container/downloadHubContainer';
import DownloadHubAlert from '../downloadHub/downloadHubAlert';

const TopBar: FC<TopBarProps> = (props: TopBarProps) => {
    const {
        imageSrc,
        userName,
        onLogout,
        setIsContactSupportOpen,
        newNotification,
        onSurveyClick,
        onSwitchBack,
        onShowDownloadHubExtended,
        downloadHubAlert,
        closeDownloadHubAlert
    } = props;
    const POLLING_INTERVAL = 5000;

    const [showDropDown, setShowDropDown] = useState<boolean>(false);
    const [showNotifications, setShowNotifications] = useState<boolean>(false);
    const [isDownloadHubOpen, setIsDownloadHubOpen] = useState<boolean>(false);
    const [downloadHubStatus, setDownloadHubStatus] = useState<keyof typeof DownloadHubButtonStatus>();
    const [reportsStatus, setReportsStatus] = useState<ReportToStatus[]>([]);

    const token = useSelector(selectToken);
    const customBrandName = useSelector(selectCustomBrandName);
    const { data: reportStatusData } = useGetReportsStatus(token, true, POLLING_INTERVAL);

    const onSwitchChange = () => {
        onSwitchBack?.(true);
        mixpanelTrack({
            eventName: 'Admin Switcher Toggle',
            properties: {
                Direction: 'Old'
            }
        });
        gainsightTrack('Admin Switcher Toggle', {
            Direction: 'Old'
        });
    };

    useEffect(() => {
        if (downloadHubAlert && downloadHubAlert.status) {
            setDownloadHubStatus(downloadHubAlert.status);
        }
    }, [downloadHubAlert]);

    useEffect(() => {
        if (isDownloadHubOpen) {
            mixpanelTrack({
                eventName: 'Download Hub Opened'
            });
            gainsightTrack('Download Hub Opened', {});
        }
    }, [isDownloadHubOpen]);

    useEffect(() => {
        if (reportStatusData?.reports) {
            if (reportsStatus.length && reportsStatus !== reportStatusData.reports) {
                const statusChanged = reportsStatus.find(reportStatus => {
                    const nextReportStatus = reportStatusData.reports.find(reportStatusData => {
                        return (
                            reportStatusData.uuid == reportStatus.uuid &&
                            reportStatusData.status == ReportStatus.COMPLETED &&
                            !reportStatusData.isViewed
                        );
                    });
                    return reportStatus.status == ReportStatus.IN_PROGRESS && nextReportStatus;
                });
                const inProgress = reportStatusData.reports.find(
                    reportStatus => reportStatus.status == ReportStatus.IN_PROGRESS
                );
                if (statusChanged) {
                    setDownloadHubStatus(DownloadHubButtonStatus.newSuccess);
                } else if (inProgress) {
                    setDownloadHubStatus(DownloadHubButtonStatus.inProgress);
                } else {
                    setDownloadHubStatus(undefined);
                }
            }
            setReportsStatus(reportStatusData.reports);
        }
    }, [reportStatusData]);

    useEffect(() => {
        if (isDownloadHubOpen && downloadHubAlert?.message) {
            closeDownloadHubAlert?.();
        }
    }, [isDownloadHubOpen]);

    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const notificationsRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const rightSideRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const navigate = useNavigate();

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (showDropDown && ref.current && !ref.current.contains(e.target)) {
                setShowDropDown(false);
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);

        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [showDropDown]);

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(e.target)) {
                if ((rightSideRef.current && !rightSideRef.current.contains(e.target)) || !rightSideRef.current) {
                    setShowNotifications(false);
                }
            }
        };

        document.addEventListener('mousedown', checkIfClickedOutside);

        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [showNotifications]);

    const logout = (e: any) => {
        e.stopPropagation();
        onLogout();
    };

    const openContactForm = () => {
        setIsContactSupportOpen(true);
        mixpanelTrack({
            eventName: 'Contact Form Opened',
            properties: {
                'Form Trigger': 'Admin'
            }
        });
        gainsightTrack('Contact Form Opened', {
            'Form Trigger': 'Admin'
        });
    };

    const imageName = getLogoNameBrandName(customBrandName || undefined);

    return (
        <div className={styles['admin-top-bar']}>
            <div className={styles['left-side']}>
                {
                    <img
                        src={`${process.env.PUBLIC_URL}/images/${imageName}.svg`}
                        className={styles.logo}
                        alt="Codebashing Logo"
                    />
                }
            </div>
            <div className={styles['right-side']}>
                <div className={styles['feedback-button-wrapper']}>
                    <img alt="" src={`${process.env.PUBLIC_URL}/images/oldVersionTop.svg`} className={styles.image} />
                    <div className={styles.text}>{translate('MakeYourVoiceHeard')}</div>
                    <Icon name="heart" />
                    <div className={styles.button}>
                        <div className={styles['button-text']} onClick={() => onSurveyClick?.()}>
                            {translate('LeaveFeedback')}
                        </div>
                    </div>
                </div>
                <div className={styles['switch-back-wrapper']}>
                    <div className={styles.text}>{translate('SwitchBackToOldVersion')}</div>
                    <AdminSmallSwitch checked={true} onChange={() => onSwitchChange()} />
                </div>
                <div className={styles['download-hub-wrapper']}>
                    <DownloadHubButton
                        onClick={() => setIsDownloadHubOpen(!isDownloadHubOpen)}
                        isOpen={isDownloadHubOpen}
                        status={downloadHubStatus}
                    />
                    {downloadHubAlert?.message && closeDownloadHubAlert && (
                        <div className={styles['download-hub-alert']}>
                            <DownloadHubAlert
                                message={downloadHubAlert.message}
                                action={downloadHubAlert.action}
                                actionIcon={downloadHubAlert.actionIcon}
                                actionMessage={downloadHubAlert.actionMessage}
                                closeDownloadHubAlert={closeDownloadHubAlert}
                            />
                        </div>
                    )}
                </div>
                {isDownloadHubOpen && (
                    <div className={styles['download-hub-items']}>
                        <DownloadHubContainer
                            isPopup={true}
                            onShowDownloadHubExtended={onShowDownloadHubExtended}
                            setIsDownloadHubOpen={setIsDownloadHubOpen}
                            handleDownloadHubStatus={setDownloadHubStatus}
                            reportStatusData={reportsStatus}
                        />
                    </div>
                )}
                <div className={styles['user-wrapper']}>
                    <img src={imageSrc} className={styles.image} alt="profile" />
                    <div
                        className={styles['user-name-wrapper']}
                        onClick={() => setShowDropDown(!showDropDown)}
                        ref={ref}
                    >
                        <div>{userName}</div>
                        <Icon name="chevronDown" className={styles['arrow-icon']} />
                        {showDropDown && (
                            <div className={styles['user-actions-wrapper']}>
                                <div className={styles['action-wrapper']} onClick={() => navigate('/app/home')}>
                                    <Icon name="account" className={styles.icon} />
                                    <div className={styles.text}>{translate('UserWorkspace')}</div>
                                </div>
                                <div className={styles['action-wrapper']} onClick={() => openContactForm()}>
                                    <Icon name="support" className={styles.icon} />
                                    <div className={styles.text}>{translate('ContactSupport')}</div>
                                </div>
                                <div className={styles['action-wrapper']} onClick={e => logout(e)}>
                                    <Icon name="logout" className={styles.icon} />
                                    <div className={styles.text}>{translate('Logout')}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className={
                        showNotifications ? styles['notification-wrapper-clicked'] : styles['notification-wrapper']
                    }
                    id="notification-wrapper"
                >
                    <Icon
                        key={newNotification ? 'newNotification' : 'notification'}
                        name={newNotification ? 'newNotification' : 'notification'}
                        className={styles.icon}
                        onClick={() => setShowNotifications(!showNotifications)}
                    />
                </div>
                {showNotifications && (
                    <NotificationsContainer refFunction={notificationsRef} rightSideRef={rightSideRef} />
                )}
            </div>
        </div>
    );
};

export default TopBar;

interface TopBarProps {
    imageSrc: string;
    userName: string;
    onLogout: Function;
    setIsContactSupportOpen: Function;
    newNotification: boolean;
    onSurveyClick?: Function;
    onSwitchBack?: Function;
    downloadHubStatus?: keyof typeof DownloadHubButtonStatus;
    onShowDownloadHubExtended?: Function;
    downloadHubAlert?: {
        message: string;
        actionIcon?: string;
        actionMessage?: string;
        action?: Function;
        status?: keyof typeof DownloadHubButtonStatus;
    };
    closeDownloadHubAlert?: Function;
}
